// Centralised GA4 event helpers. Import named functions instead of calling
// gtag() directly — that keeps event-name / param shapes consistent and
// lets us instrument new surfaces by just adding a helper here.
//
// Event taxonomy (top-of-funnel → revenue):
//   page_view            — fired by components/consent/pixel-loader.tsx when analytics consent is granted
//   scroll_depth         — global listener (25, 50, 75, 100%)
//   outbound_link        — global click listener on external anchors
//   cta_click            — generic CTA click — pass {cta, surface, destination}
//   tool_open            — visitor lands on a free-tool surface
//   tool_use             — visitor actually interacts with a tool (clicks Run, slides slider, etc.)
//   select_plan          — pricing tile click — pass {plan, billing_cycle}
//   book_demo_click      — Apollo / demo CTA click
//   sign_up_start        — app.idukki.io/register click — pre-account creation
//   generate_lead        — lead-capture form successful submit
//   newsletter_signup    — footer newsletter submit
//   theme_change         — theme switcher selection
//   widget_layout_view   — visitor views a layout preview tile
//   gallery_view         — a shoppable gallery / live preview scrolls into view — pass {surface, gallery?}
//   gallery_interaction  — tile / hotspot click inside a gallery — pass {action, surface, gallery?, product?}
//   compare_open         — visitor opens a /compare/[slug] page
//   search               — visitor uses any on-site search/find input

export type GAParams = Record<string, string | number | boolean | undefined | null>

function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return
  const w = window as Window & { gtag?: (...a: unknown[]) => void; dataLayer?: unknown[] }
  if (typeof w.gtag === 'function') {
    w.gtag(...args)
  } else {
    // Queue the call onto dataLayer; gtag() shim in ga4.tsx flushes it
    // once the loader fires.
    w.dataLayer = w.dataLayer || []
    w.dataLayer.push(args)
  }
}

export function track(event: string, params: GAParams = {}): void {
  // Drop falsy params so GA4 doesn't see "undefined" strings in the report.
  const clean: GAParams = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    clean[k] = v
  }
  gtag('event', event, clean)
}

// ─── Named helpers (one per conversion event) ─────────────────────────────

export function trackCta(opts: { cta: string; surface: string; destination?: string }) {
  track('cta_click', opts)
}
export function trackToolOpen(slug: string) {
  track('tool_open', { tool: slug })
}
export function trackToolUse(slug: string, action: string, extra: GAParams = {}) {
  track('tool_use', { tool: slug, action, ...extra })
}
export function trackSelectPlan(opts: { plan: string; billing_cycle?: 'monthly' | 'annual'; price?: number }) {
  track('select_plan', opts)
}
export function trackBookDemo(surface: string) {
  track('book_demo_click', { surface })
}
export function trackSignUpStart(surface: string) {
  track('sign_up_start', { surface })
}
export function trackGenerateLead(opts: { intent?: string; source?: string }) {
  track('generate_lead', opts)
}
export function trackNewsletterSignup(surface: string) {
  track('newsletter_signup', { surface })
}
export function trackThemeChange(theme: string) {
  track('theme_change', { theme })
}
export function trackWidgetLayoutView(layout: string) {
  track('widget_layout_view', { layout })
}
export function trackGalleryView(opts: { surface: string; gallery?: string; layout?: string }) {
  track('gallery_view', opts)
}
export function trackGalleryInteraction(opts: {
  action: 'tile_click' | 'shop_click' | 'pin_click'
  surface: string
  gallery?: string
  product?: string
  creator?: string
}) {
  track('gallery_interaction', opts)
}
export function trackCompareOpen(slug: string) {
  track('compare_open', { competitor: slug })
}
export function trackSearch(opts: { query: string; surface: string; result_count?: number }) {
  track('search', opts)
}
export function trackOutbound(opts: { href: string; surface: string }) {
  track('outbound_link', opts)
}
export function trackScrollDepth(depth: 25 | 50 | 75 | 100) {
  track('scroll_depth', { depth })
}
